/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import FullScreenDialog from '../Dialog/FullScreenDialog';
import UsersTable from './usersTable';
import { AlertDialog } from '../../../shared/Dialog/AlertDialog';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsersByGroup } from '../../../store/userGroups/usersByGroup';
import {
  ADD_USERS,
  DELETE_USER,
  DELETE_USER_CONTENT,
  CANCEL,
  EMPTY_SCREEN_TITLE_USERS,
  DELETE_USER_FROM_GROUP,
  USER_GROUPS_DELETE_SUCCESS_MESSAGE,
  NEXT,
  DELETE_USERS
} from '../constants';
import { useStyles } from './styles';
import { deleteUserFromGroup } from '../../../services/userGroups';
import { displaySnackbar } from '../../../store/snackBar/snackBarActionCreator';
import {
  selectPaginationDetails,
  selectUsersByGroup,
  selectUsersByGroupError,
  selectUsersByGroupLoading
} from '../../../store/userGroups/userGroupSelector';
import { ErrorMessage } from "../../../shared/ErrorMessage/ErrorMessage";
import { checkResourcePermission } from '../../../shared/CheckPermissions/CheckPermission';
import { actions, resources } from '../../../shared/CheckPermissions/constants';
import { selectResourcePermissions } from '../../../store/auth/authSelector';
import interpolateString from '../../../utility/stringInterpolation';
import { 
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton 
} from 'material-react-table';
import { LuiEmptyState, LuiToolbar} from '@jda/lui-common-component-library-mui5';
import { UserAdd } from '@jda/lui-common-icon-library-mui5';

const UsersGroups = ({ identifier, goToAssignRolesTab }: any) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const error = useSelector(selectUsersByGroupError);
  const usersByGroup = useSelector(selectUsersByGroup);
  const loading = useSelector(selectUsersByGroupLoading);
  const pageDetails = useSelector(selectPaginationDetails);
  const resourcePermissions = useSelector(selectResourcePermissions);

  const [open, setOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [openDeleteUser, setOpenDeleteUser] = useState(false);
  const [deletingUsersByGroupDetails, setDeletingUsersByGroupDetails] = useState<any>([]);

  const addUserPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.CREATE, resourcePermissions.resources[resources.GROUPS_SUBJECTS]);
  const deleteUserPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.DELETE, resourcePermissions.resources[resources.GROUPS_SUBJECTS]);

  const handleChangePageOptions = (itemsPerPage: number, page: number) => {
    const search = {
      'page-number': page,
      'page-size': itemsPerPage,
    };
    fetchUsersByGroup(dispatch, identifier, search);
  };

  useEffect(() => {
    if (identifier) {
      const search = {
        'page-number': 1,
        'page-size': pageDetails.pageSize,
      };
      fetchUsersByGroup(dispatch, identifier, search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, identifier]);

  const onUserDelete = (data: any) => {
    setOpenDeleteUser(true);
    setDeletingUsersByGroupDetails(data);
  };

  const handleDeleteUser = async () => {
    const listOfUsersForDelete = deletingUsersByGroupDetails.map((user: any) => user.subjectId);
    try {
      await deleteUserFromGroup(identifier, [...listOfUsersForDelete]);
      setOpenDeleteUser(false);
      setSelectedRows([]);
      setDeletingUsersByGroupDetails([]);
      const search = {
        'page-number': pageDetails.pageNumber,
        'page-size': pageDetails.pageSize
      };
      dispatch(displaySnackbar(`${USER_GROUPS_DELETE_SUCCESS_MESSAGE} ${identifier} group`, 'success'));
      const { data, metadata } = await fetchUsersByGroup(dispatch, identifier, search);

      if (data.length === 0 && metadata.totalCount > 0) {
        const pageOneApi = {
          'page-number': 1,
          'page-size': pageDetails.pageSize
        };
        fetchUsersByGroup(dispatch, identifier, pageOneApi);
      }
    } catch (error: any) {
      dispatch(displaySnackbar(error.response?.data?.message || error.message, 'error'));
    }
  };

  const handleClickOpenAddUsers = () => setOpen(true);

  const handleCloseAddUsers = () => setOpen(false);

  if (error !== null) {
    return <ErrorMessage message={error} />;
  };


  const extendedToolbar = ({ table }) => {
    const leftComponents = {
      component: (
        <Box />
      )
    };
    const rightComponents = {
      component: (
        <Box>
          {addUserPermission &&
            <Button
              id="add-user-button"
              size="small"
              variant="outlined"
              color="primary"
              disableElevation
              startIcon={<UserAdd />}
              onClick={handleClickOpenAddUsers}
              data-testid="add-user-button"
              className={classes.addUserButton}
            >
              {ADD_USERS}
            </Button>
          }
          {selectedRows.length > 0 && (
            <Button
              color="primary"
              size="small"
              variant="contained"
              name="delete-users"
              data-testid="delete-users"
              className={classes.deleteButton}
              onClick={() => onUserDelete(selectedRows)}
              disabled={selectedRows.length > 500 ? true : false}
            >
              {interpolateString([selectedRows.length], DELETE_USERS)}
            </Button>
          )}
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
        </Box>
      )
    };
    return <LuiToolbar className={classes.toolbarPanel} leftZone={leftComponents} rightZone={rightComponents} />;
  };
  const emptyTableFallback = () => {
    return (
      <Box my={5}>
        <LuiEmptyState imageItem={null} heading={ADD_USERS} subHeading={EMPTY_SCREEN_TITLE_USERS} />
      </Box>
    );
  };
  return (
    <Box className={classes.root}>

      <UsersTable
        loading={loading}
        pageDetails={pageDetails}
        selectedRows={selectedRows}
        usersByGroup={usersByGroup}
        onUserDelete={onUserDelete}
        extendedToolbar={extendedToolbar}
        setSelectedRows={setSelectedRows}
        deleteUserPermission={deleteUserPermission}
        handleChangePageOptions={handleChangePageOptions}
        renderEmptyState={emptyTableFallback}
      />

      {open && <FullScreenDialog open={open} identifier={identifier} handleClose={handleCloseAddUsers} />}

      <Box>
        <Button
          color="primary"
          variant="contained"
          onClick={goToAssignRolesTab}
          data-testid="next-button"
          className={classes.nextButton}
        >
          {NEXT}
        </Button>
      </Box>

      {openDeleteUser && (
        <AlertDialog
          isStatement={true}
          showDialogLoader={false}
          secondButtonText={CANCEL}
          handleOnOk={handleDeleteUser}
          primaryButtonText={DELETE_USER}
          primaryButtonColor="error"
          dialogTitle={DELETE_USER_CONTENT}
          dialogContentText={DELETE_USER_FROM_GROUP}
          recordName={deletingUsersByGroupDetails.length === 1 ?
            deletingUsersByGroupDetails[0].displayName : `${deletingUsersByGroupDetails.length} users`}
          handleOnCancel={() => {
            setOpenDeleteUser(false);
            setDeletingUsersByGroupDetails([]);
          }}
          additionalContent={
            <Typography component="span">
              {' '}
              from <b>{identifier}</b>?
            </Typography>
          }
        />
      )}
    </Box>
  );
};

export default UsersGroups;
